import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  PropsWithChildren,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';

export type CardDotVariant = 'default' | 'green';

export function Card(
  props: PropsWithChildren<{
    title: ReactNode;
    hint?: ReactNode;
    dotVariant?: CardDotVariant;
    ariaLabel?: string;
    headerRight?: ReactNode;
  }>
) {
  const { title, hint, dotVariant = 'default', ariaLabel, headerRight, children } = props;

  return (
    <section className="card" aria-label={ariaLabel}>
      <div className="cardHeader">
        <h2 className="cardTitle">
          <span className={dotVariant === 'green' ? 'dot green' : 'dot'} />
          {title}
        </h2>
        {headerRight ? headerRight : hint ? <p className="hint">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

export function Stack(props: PropsWithChildren) {
  return <div className="stack">{props.children}</div>;
}

export function Row(props: PropsWithChildren<{ tight?: boolean }>) {
  return <div className={props.tight ? 'row tight' : 'row'}>{props.children}</div>;
}

export function Divider() {
  return <div className="divider" />;
}

export function Field(
  props: PropsWithChildren<{
    label?: ReactNode;
    htmlFor?: string;
    labelRight?: ReactNode;
  }>
) {
  const { label, htmlFor, labelRight, children } = props;

  return (
    <div className="field">
      {label ? (
        labelRight ? (
          <div className="labelRow">
            <label className="label" htmlFor={htmlFor}>
              {label}
            </label>
            {labelRight}
          </div>
        ) : (
          <label className="label" htmlFor={htmlFor}>
            {label}
          </label>
        )
      ) : null}
      {children}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={['input', props.className].filter(Boolean).join(' ')} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea {...props} className={['textarea', props.className].filter(Boolean).join(' ')} />
  );
}

export function Button(
  props: ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'primary' | 'secondary';
  }
) {
  const { variant = 'primary', className, ...rest } = props;
  const variantClass = variant === 'secondary' ? 'secondary' : undefined;

  return (
    <button {...rest} className={['btn', variantClass, className].filter(Boolean).join(' ')} />
  );
}

export function CodeBlock(props: { children: ReactNode }) {
  return <pre className="code">{props.children}</pre>;
}

export function Empty(props: { children?: ReactNode }) {
  return <p className="empty">{props.children ?? '(empty)'}</p>;
}

export function Items(props: PropsWithChildren) {
  return <div className="items">{props.children}</div>;
}

export function ItemCard(props: PropsWithChildren<{ title: ReactNode }>) {
  return (
    <div className="itemCard">
      <p className="itemTitle">{props.title}</p>
      {props.children}
    </div>
  );
}

export function ItemMeta(props: PropsWithChildren) {
  return <p className="itemMeta">{props.children}</p>;
}

export function List(props: PropsWithChildren<{ as?: 'ol' | 'ul' }>) {
  const Tag = props.as ?? 'ol';
  return <Tag className="list">{props.children}</Tag>;
}
